/**
 * The upload half of the attachment paths (homework submissions and video attachments).
 *
 * The Supabase client is pointed at the /api rewrite in vercel.json rather than at
 * Supabase directly. Vercel answers any request body over 4.5MB with a 413
 * FUNCTION_PAYLOAD_TOO_LARGE whose body is text/plain, so a large file never reached
 * Storage (whose own limit is 50MB) and storage-js — which parses the response before it
 * checks the status — turned that into a bare SyntaxError.
 *
 * Small files keep the proxied path. Large ones ask the proxied client for a signed
 * upload URL (a small JSON round-trip, and still where Storage RLS is evaluated) and then
 * send only the bytes straight to Supabase with the token that grants.
 */

import { supabase, supabaseDirect } from '../lib/supabase'
import { PROXY_BODY_LIMIT, describeUploadError, guessMimeType } from './attachmentLimits'

// storage-js wraps a File in FormData and takes the part's content type from the File
// itself, so its `contentType` option is ignored for File bodies — the type has to be set
// on the file. An untyped .pptx would otherwise be stored as application/octet-stream and
// rejected by the bucket's allowed_mime_types.
function withResolvedType(file: File): File {
  const mimeType = guessMimeType(file)
  if (!mimeType || file.type === mimeType) return file
  return new File([file], file.name, { type: mimeType, lastModified: file.lastModified })
}

/** Uploads one attachment and returns its storage path, as `upload()` would. */
export async function uploadAttachmentFile(params: {
  bucket: string
  path: string
  file: File
}): Promise<{ path: string }> {
  const { bucket, path } = params
  const file = withResolvedType(params.file)

  if (file.size <= PROXY_BODY_LIMIT) {
    try {
      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(path, file, { cacheControl: '3600', upsert: false })
      if (error) throw error
      return { path: data.path }
    } catch (err) {
      throw new Error(describeUploadError(err))
    }
  }

  const { data: signed, error: signError } = await supabase.storage
    .from(bucket)
    .createSignedUploadUrl(path)
  if (signError || !signed) throw new Error(describeUploadError(signError))

  try {
    const { data, error } = await supabaseDirect.storage
      .from(bucket)
      .uploadToSignedUrl(signed.path, signed.token, file, { cacheControl: '3600' })
    if (error) throw error
    if (!data) throw new Error('アップロードに失敗しました')
    return { path: data.path }
  } catch (err) {
    // Reaching Supabase directly can fail outright on a network that blocks the host, in
    // which case there is no status code — only the rejected fetch.
    throw new Error(describeUploadError(err))
  }
}
